import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { GET_CREDIT_SUBMISSION_DECLINE_REASONS } from 'modules/creditSubmission/api/getCreditSubmissionDeclineReason';
import { CreditSubmissionDeclindeReasonsResponse } from 'modules/creditSubmission/types';
import { getSubmissionDeclineReasonsOptions } from 'modules/creditSubmission/lib';
import { SectionTitle } from './SectionTitle';
import { CreditSubmission } from '@trnsact/trnsact-shared-types';

interface Props {
  parsedReasons: string[];
  creditSubmission: CreditSubmission & {
    creditSubmissionDeclineReasonId?: string | null;
  };
}

export const DeclineReasonsCard = ({ parsedReasons, creditSubmission }: Props) => {
  const lenderProfiles = useSelector((state: any) => state.lp); //

  const lenderAccountId = lenderProfiles.find((lp: any) => lp.lenderProfileId === creditSubmission.lenderProfileId)
    ?.lenderAccount.id;

  const { data: declineReasonsData } = useQuery<CreditSubmissionDeclindeReasonsResponse>(
    GET_CREDIT_SUBMISSION_DECLINE_REASONS,
    {
      variables: { accountId: lenderAccountId },
      skip: !creditSubmission.creditSubmissionDeclineReasonId || !lenderAccountId,
    }
  );

  const allReasons = useMemo(() => {
    const reasonsList = [...parsedReasons];

    if (creditSubmission.creditSubmissionDeclineReasonId && declineReasonsData?.creditSubmissionDeclineReasons) {
      const match = getSubmissionDeclineReasonsOptions(declineReasonsData.creditSubmissionDeclineReasons).find(
        r => r.value === creditSubmission.creditSubmissionDeclineReasonId
      );
      if (match && !reasonsList.includes(match.label)) {
        reasonsList.unshift(match.label);
      }
    }

    return reasonsList;
  }, [parsedReasons, creditSubmission.creditSubmissionDeclineReasonId, declineReasonsData]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: grey[300],
        backgroundColor: grey[50],
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <SectionTitle>Decline Reasons</SectionTitle>
      {allReasons.length > 0 ? (
        <Stack gap={1}>
          {allReasons.map((reason, idx) => (
            <Box
              key={idx}
              sx={{
                py: 1,
                borderBottom: idx < allReasons.length - 1 ? '1px solid' : 'none',
                borderColor: 'rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="body1">{reason}</Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="subtitle2" color="text.secondary" component={'p'}>
          No decline reasons provided.
        </Typography>
      )}
    </Paper>
  );
};
